import { useForm } from "react-hook-form";
import { useState, useCallback } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { RecordingEntry } from "@/modules/common/types/audio.types";
import { useRecordingManager } from "@/modules/common/hooks/audio";
import { showToast } from "@/modules/common/utils";
import { TOAST_IDS, ToastType } from "@/modules/common/types";

const MAX_RECORDING_DURATION_SECONDS = 3 * 60; // 3 minutes

const expoMeetingFormSchema = z
  .object({
    comment: z.string().optional(),
    hasRecordings: z.boolean().default(false),
  })
  .refine(
    (data) => {
      const hasComment = data.comment && data.comment.trim().length >= 4;
      return hasComment || data.hasRecordings;
    },
    {
      message: "Please record a voice note or add a comment to continue.",
      path: ["comment"],
    },
  );

type ExpoMeetingFormData = z.infer<typeof expoMeetingFormSchema>;

interface UseExpoMeetingFormProps {
  handleFormSubmission: (formattedData: any, formType: "sale" | "form") => void;
}

const useExpoMeetingForm = ({
  handleFormSubmission,
}: UseExpoMeetingFormProps) => {
  const [recordings, setRecordings] = useState<RecordingEntry[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [showAddCommentSection, setShowAddCommentSection] = useState(false);
  const [addCommentText, setAddCommentText] = useState("");

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
    trigger,
  } = useForm<ExpoMeetingFormData>({
    resolver: zodResolver(expoMeetingFormSchema),
    defaultValues: {
      comment: "",
      hasRecordings: false,
    },
    mode: "onSubmit",
  });

  const comment = watch("comment");
  const hasRecordings = watch("hasRecordings");

  const updateRecordings = useCallback(
    (newRecordings: RecordingEntry[]) => {
      setRecordings(newRecordings);
      setValue("hasRecordings", newRecordings.length > 0);
      if (hasAttemptedSubmit) trigger();
    },
    [setValue, trigger, hasAttemptedSubmit],
  );

  const handleRecordingsChange = useCallback(
    (recordings: any[]) => {
      updateRecordings(recordings);
      if (recordings.length > 0) {
        setShowAddCommentSection(false);
        setAddCommentText("");
      }
    },
    [updateRecordings],
  );

  const handleMaxDurationReached = useCallback(() => {
    const maxMinutes = Math.floor(MAX_RECORDING_DURATION_SECONDS / 60);
    showToast({
      type: ToastType.WARNING,
      message: `Maximum recording duration reached (${maxMinutes} minutes). Recording saved automatically.`,
      toastId: TOAST_IDS.EDIT_LEAD_ERROR,
      options: { position: "bottom-left", autoClose: 5000 },
    });
  }, []);

  const recording = useRecordingManager({
    maxRecordings: 3,
    onRecordingsChange: handleRecordingsChange,
    maxDuration: MAX_RECORDING_DURATION_SECONDS,
    onMaxDurationReached: handleMaxDurationReached,
  });

  const onSubmit = async (
    data: ExpoMeetingFormData,
    addCommentText?: string,
  ) => {
    setIsSubmitting(true);
    try {
      const formattedData: any = {
        recordings: recordings.map((r) => ({
          audioBlob: r.audioBlob,
          duration: r.duration,
        })),
      };

      // Use addCommentText if provided (when there are recordings), otherwise use initial comment
      const commentText = addCommentText?.trim() || data.comment?.trim();

      if (commentText && commentText.length > 0) {
        formattedData.comment = commentText;
      }

      handleFormSubmission(formattedData, "form");
    } catch (error) {
      console.error("Error submitting expo meeting form:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFormSubmit = (
    e?: React.BaseSyntheticEvent,
    addCommentText?: string,
  ) => {
    setHasAttemptedSubmit(true);
    return handleSubmit((data) => onSubmit(data, addCommentText))(e);
  };

  const onFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleFormSubmit(e, addCommentText);
  };

  const canSubmit = (comment && comment.trim().length >= 4) || hasRecordings;

  const isTypingInInitialComment = comment && comment.trim().length > 0;
  const isTypingInAddComment = addCommentText.trim().length > 0;

  return {
    formControls: {
      control,
      errors: hasAttemptedSubmit ? errors : {},
    },
    formHandlers: {
      handleSubmit: handleFormSubmit,
      onSubmit,
      onFormSubmit,
    },
    formState: {
      comment,
      hasRecordings,
      recordings,
      updateRecordings,
      isSubmitting,
      canSubmit,
      hasAttemptedSubmit,
    },
    recording,
    commentState: {
      showAddCommentSection,
      setShowAddCommentSection,
      addCommentText,
      setAddCommentText,
    },
    computed: {
      isTypingInInitialComment,
      isTypingInAddComment,
    },
  };
};

export default useExpoMeetingForm;
